import {
  useState, useEffect, useRef, useCallback, forwardRef, useImperativeHandle,
} from 'react';
import PropTypes from 'prop-types';

/**
 * PuzzleEngineV2 - A custom puzzle engine that slices images directly.
 * No pattern filling = no tiling issues.
 * Features: group connections, animations, sound, zoom/pan
 */

// Configuration
const BRAND_COLORS = ['#4CAF50', '#2196F3', '#FFC107', '#F44336'];
const SNAP_PITCHES = [880, 988, 1047, 1175, 1319]; // Musical pitches for snap sound
const MIN_ZOOM = 1;
const MAX_ZOOM = 3;

// Piece shape style presets
const PIECE_STYLES = {
  traditional: {
    tabSize: 0.2, neckRatio: 0.4, widthRatio: 1.8, bulgeRatio: 0.6,
  },
  rounded: {
    tabSize: 0.18, neckRatio: 1.0, widthRatio: 2.0, bulgeRatio: 0.55,
  },
  square: {
    tabSize: 0.15, neckRatio: 1.0, widthRatio: 1.5, bulgeRatio: 0,
  },
};

// Map legacy piece styles to new styles (for existing puzzles)
const mapLegacyStyle = (style) => {
  const legacyMap = {
    classic: 'traditional',
    interlocking: 'traditional',
    wavy: 'rounded',
    random: 'traditional',
  };
  return legacyMap[style] || style;
};

// Get device tier for save state (matches PuzzleCanvas)
// Uses User Agent detection to return 'mobile', 'tablet', or 'desktop'
// These values must match the database enum in DailyPuzzleProgress schema
const getDeviceTier = () => {
  if (typeof window === 'undefined') return 'desktop';
  const ua = navigator.userAgent.toLowerCase();

  // Check for tablets first (iPad, Android tablets, etc.)
  // iPads always have "iPad" in UA, Android tablets have "Android" but NOT "Mobile"
  if (/ipad/.test(ua) || (/android/.test(ua) && !/mobile/.test(ua))) {
    return 'tablet';
  }

  // Check for mobile devices (phones)
  // These have specific identifiers or "Mobile" in UA
  if (/android|webos|iphone|ipod|blackberry|iemobile|opera mini/.test(ua)) {
    return 'mobile';
  }

  // Everything else is desktop
  return 'desktop';
};

// Shared AudioContext for connection sounds
let sharedAudioContext = null;

// Generate a crisp "snap" sound using Web Audio API
const playConnectSound = () => {
  try {
    if (!sharedAudioContext) {
      const AudioContextClass = window.AudioContext || window.webkitAudioContext;
      sharedAudioContext = new AudioContextClass();
    }
    const ctx = sharedAudioContext;

    if (ctx.state === 'suspended') {
      ctx.resume();
    }

    const now = ctx.currentTime;
    const basePitch = SNAP_PITCHES[Math.floor(Math.random() * SNAP_PITCHES.length)];
    const pitch = basePitch + (Math.random() - 0.5) * 60;

    const osc = ctx.createOscillator();
    const gain = ctx.createGain();

    osc.connect(gain);
    gain.connect(ctx.destination);

    osc.frequency.setValueAtTime(pitch, now);
    osc.frequency.exponentialRampToValueAtTime(pitch * 0.5, now + 0.03);
    osc.type = 'sine';

    gain.gain.setValueAtTime(0, now);
    gain.gain.linearRampToValueAtTime(0.2, now + 0.003);
    gain.gain.exponentialRampToValueAtTime(0.01, now + 0.04);

    osc.start(now);
    osc.stop(now + 0.05);
  } catch (err) {
    // Silently fail if audio not available
  }
};

// Calculate piece size so ALL pieces fit within canvas when shuffled
// (matches PuzzleCanvas.js exactly for production-identical sizing)
const calculatePieceSize = (canvasWidth, canvasHeight, cols, rows) => {
  const totalPieces = cols * rows;
  const canvasAspect = canvasWidth / canvasHeight;
  const bestGridCols = Math.ceil(Math.sqrt(totalPieces * canvasAspect));
  let bestGridRows = Math.ceil(totalPieces / bestGridCols);

  while (bestGridCols * bestGridRows < totalPieces) {
    bestGridRows += 1;
  }

  const maxVisualWidth = canvasWidth / bestGridCols;
  const maxVisualHeight = canvasHeight / bestGridRows;
  const maxVisualSize = Math.min(maxVisualWidth, maxVisualHeight);

  // Match PuzzleCanvas: visual size = pieceSize * 1.8 (headbreaker tab sizing)
  const calculatedSize = Math.floor(maxVisualSize / 1.8);

  // maxPieceSize = 21% of width, capped 75-315px (50% larger than original)
  const maxPieceSize = Math.min(Math.max(Math.floor(canvasWidth * 0.21), 75), 315);
  const minPieceSize = 15;

  return Math.max(minPieceSize, Math.min(calculatedSize, maxPieceSize));
};

// Generate SVG path for jigsaw piece
// edges = { top: 'flat'|'tab'|'slot', right: ..., bottom: ..., left: ... }
// style = 'traditional' | 'rounded' | 'square'
const generateSvgPath = (edges, width, height, style = 'traditional') => {
  // Get style config (fall back to traditional if unknown)
  const config = PIECE_STYLES[style] || PIECE_STYLES.traditional;
  const {
    tabSize, neckRatio, widthRatio, bulgeRatio,
  } = config;

  // Reverse calculate base dimensions from total (which includes tab margins)
  const baseWidth = width / (1 + tabSize * 2);
  const baseHeight = height / (1 + tabSize * 2);

  // Tab dimensions based on style config
  const tabHeight = Math.min(baseWidth, baseHeight) * tabSize;
  const tabWidth = tabHeight * widthRatio;
  const neckW = tabWidth * neckRatio; // Neck width (1.0 = no narrowing)
  const bulge = tabHeight * bulgeRatio; // Curve amount (0 = straight lines)
  const neckOffset = (tabWidth - neckW) / 2; // Distance from edge to neck

  // Edge positions (where tabs/slots start along each edge)
  const edgeStartX = (width - tabWidth) / 2;
  const edgeEndX = width - edgeStartX;
  const edgeStartY = (height - tabWidth) / 2;
  const edgeEndY = height - edgeStartY;
  const midX = width / 2;
  const midY = height / 2;

  // Inner boundaries (tab offset from edges)
  const innerLeft = tabHeight;
  const innerRight = width - tabHeight;
  const innerTop = tabHeight;
  const innerBottom = height - tabHeight;

  // Build SVG path starting from top-left inner corner
  let path = `M ${innerLeft} ${innerTop}`;

  // TOP EDGE (left to right)
  if (edges.top === 'flat') {
    path += ` L ${innerRight} ${innerTop}`;
  } else if (edges.top === 'slot') {
    // Slot curves DOWNWARD into piece
    const slotDepth = innerTop + tabHeight;
    const neckY = slotDepth * 0.3 + innerTop * 0.7;
    const neckLeftX = edgeStartX + neckOffset;
    const neckRightX = edgeEndX - neckOffset;
    path += ` L ${edgeStartX} ${innerTop}`;
    path += ` C ${neckLeftX} ${innerTop} ${neckLeftX} ${neckY} ${neckLeftX} ${neckY}`;
    path += ` C ${neckLeftX} ${neckY} ${midX - bulge} ${slotDepth} ${midX} ${slotDepth}`;
    path += ` C ${midX + bulge} ${slotDepth} ${neckRightX} ${neckY} ${neckRightX} ${neckY}`;
    path += ` C ${neckRightX} ${neckY} ${neckRightX} ${innerTop} ${edgeEndX} ${innerTop}`;
    path += ` L ${innerRight} ${innerTop}`;
  } else {
    // Tab extends UPWARD out of piece
    const tabTip = innerTop - tabHeight;
    const neckY = tabTip * 0.3 + innerTop * 0.7;
    const neckLeftX = edgeStartX + neckOffset;
    const neckRightX = edgeEndX - neckOffset;
    path += ` L ${edgeStartX} ${innerTop}`;
    path += ` C ${neckLeftX} ${innerTop} ${neckLeftX} ${neckY} ${neckLeftX} ${neckY}`;
    path += ` C ${neckLeftX} ${neckY} ${midX - bulge} ${tabTip} ${midX} ${tabTip}`;
    path += ` C ${midX + bulge} ${tabTip} ${neckRightX} ${neckY} ${neckRightX} ${neckY}`;
    path += ` C ${neckRightX} ${neckY} ${neckRightX} ${innerTop} ${edgeEndX} ${innerTop}`;
    path += ` L ${innerRight} ${innerTop}`;
  }

  // RIGHT EDGE (top to bottom)
  if (edges.right === 'flat') {
    path += ` L ${innerRight} ${innerBottom}`;
  } else if (edges.right === 'tab') {
    // Tab extends RIGHTWARD out of piece
    const tabTip = innerRight + tabHeight;
    const neckX = tabTip * 0.3 + innerRight * 0.7;
    const neckTopY = edgeStartY + neckOffset;
    const neckBottomY = edgeEndY - neckOffset;
    path += ` L ${innerRight} ${edgeStartY}`;
    path += ` C ${innerRight} ${neckTopY} ${neckX} ${neckTopY} ${neckX} ${neckTopY}`;
    path += ` C ${neckX} ${neckTopY} ${tabTip} ${midY - bulge} ${tabTip} ${midY}`;
    path += ` C ${tabTip} ${midY + bulge} ${neckX} ${neckBottomY} ${neckX} ${neckBottomY}`;
    path += ` C ${neckX} ${neckBottomY} ${innerRight} ${neckBottomY} ${innerRight} ${edgeEndY}`;
    path += ` L ${innerRight} ${innerBottom}`;
  } else {
    // Slot curves LEFTWARD into piece
    const slotDepth = innerRight - tabHeight;
    const neckX = slotDepth * 0.3 + innerRight * 0.7;
    const neckTopY = edgeStartY + neckOffset;
    const neckBottomY = edgeEndY - neckOffset;
    path += ` L ${innerRight} ${edgeStartY}`;
    path += ` C ${innerRight} ${neckTopY} ${neckX} ${neckTopY} ${neckX} ${neckTopY}`;
    path += ` C ${neckX} ${neckTopY} ${slotDepth} ${midY - bulge} ${slotDepth} ${midY}`;
    path += ` C ${slotDepth} ${midY + bulge} ${neckX} ${neckBottomY} ${neckX} ${neckBottomY}`;
    path += ` C ${neckX} ${neckBottomY} ${innerRight} ${neckBottomY} ${innerRight} ${edgeEndY}`;
    path += ` L ${innerRight} ${innerBottom}`;
  }

  // BOTTOM EDGE (right to left)
  if (edges.bottom === 'flat') {
    path += ` L ${innerLeft} ${innerBottom}`;
  } else if (edges.bottom === 'tab') {
    // Tab extends DOWNWARD out of piece
    const tabTip = innerBottom + tabHeight;
    const neckY = tabTip * 0.3 + innerBottom * 0.7;
    const neckRightX = edgeEndX - neckOffset;
    const neckLeftX = edgeStartX + neckOffset;
    path += ` L ${edgeEndX} ${innerBottom}`;
    path += ` C ${neckRightX} ${innerBottom} ${neckRightX} ${neckY} ${neckRightX} ${neckY}`;
    path += ` C ${neckRightX} ${neckY} ${midX + bulge} ${tabTip} ${midX} ${tabTip}`;
    path += ` C ${midX - bulge} ${tabTip} ${neckLeftX} ${neckY} ${neckLeftX} ${neckY}`;
    path += ` C ${neckLeftX} ${neckY} ${neckLeftX} ${innerBottom} ${edgeStartX} ${innerBottom}`;
    path += ` L ${innerLeft} ${innerBottom}`;
  } else {
    // Slot curves UPWARD into piece
    const slotDepth = innerBottom - tabHeight;
    const neckY = slotDepth * 0.3 + innerBottom * 0.7;
    const neckRightX = edgeEndX - neckOffset;
    const neckLeftX = edgeStartX + neckOffset;
    path += ` L ${edgeEndX} ${innerBottom}`;
    path += ` C ${neckRightX} ${innerBottom} ${neckRightX} ${neckY} ${neckRightX} ${neckY}`;
    path += ` C ${neckRightX} ${neckY} ${midX + bulge} ${slotDepth} ${midX} ${slotDepth}`;
    path += ` C ${midX - bulge} ${slotDepth} ${neckLeftX} ${neckY} ${neckLeftX} ${neckY}`;
    path += ` C ${neckLeftX} ${neckY} ${neckLeftX} ${innerBottom} ${edgeStartX} ${innerBottom}`;
    path += ` L ${innerLeft} ${innerBottom}`;
  }

  // LEFT EDGE (bottom to top)
  if (edges.left === 'flat') {
    path += ` L ${innerLeft} ${innerTop}`;
  } else if (edges.left === 'slot') {
    // Slot curves RIGHTWARD into piece
    const slotDepth = innerLeft + tabHeight;
    const neckX = slotDepth * 0.3 + innerLeft * 0.7;
    const neckBottomY = edgeEndY - neckOffset;
    const neckTopY = edgeStartY + neckOffset;
    path += ` L ${innerLeft} ${edgeEndY}`;
    path += ` C ${innerLeft} ${neckBottomY} ${neckX} ${neckBottomY} ${neckX} ${neckBottomY}`;
    path += ` C ${neckX} ${neckBottomY} ${slotDepth} ${midY + bulge} ${slotDepth} ${midY}`;
    path += ` C ${slotDepth} ${midY - bulge} ${neckX} ${neckTopY} ${neckX} ${neckTopY}`;
    path += ` C ${neckX} ${neckTopY} ${innerLeft} ${neckTopY} ${innerLeft} ${edgeStartY}`;
    path += ` L ${innerLeft} ${innerTop}`;
  } else {
    // Tab extends LEFTWARD out of piece
    const tabTip = innerLeft - tabHeight;
    const neckX = tabTip * 0.3 + innerLeft * 0.7;
    const neckBottomY = edgeEndY - neckOffset;
    const neckTopY = edgeStartY + neckOffset;
    path += ` L ${innerLeft} ${edgeEndY}`;
    path += ` C ${innerLeft} ${neckBottomY} ${neckX} ${neckBottomY} ${neckX} ${neckBottomY}`;
    path += ` C ${neckX} ${neckBottomY} ${tabTip} ${midY + bulge} ${tabTip} ${midY}`;
    path += ` C ${tabTip} ${midY - bulge} ${neckX} ${neckTopY} ${neckX} ${neckTopY}`;
    path += ` C ${neckX} ${neckTopY} ${innerLeft} ${neckTopY} ${innerLeft} ${edgeStartY}`;
    path += ` L ${innerLeft} ${innerTop}`;
  }

  path += ' Z';
  return path;
};

// Slice image into pieces
// basePieceSize = target display size for each piece (the "base" before tab extensions)
const sliceImage = (img, rows, cols, basePieceSize, pieceStyle = 'traditional') => {
  // Get tab size from style config (must match generateSvgPath)
  const styleConfig = PIECE_STYLES[pieceStyle] || PIECE_STYLES.traditional;
  const tabRatio = styleConfig.tabSize;

  // Source piece dimensions from original image
  const sourcePieceWidth = img.naturalWidth / cols;
  const sourcePieceHeight = img.naturalHeight / rows;

  // Display dimensions - base size plus tab extensions on each side
  const displayBaseWidth = basePieceSize;
  const displayBaseHeight = basePieceSize * (sourcePieceHeight / sourcePieceWidth);

  // Calculate tab radius using min to keep tabs square (matches generateSvgPath)
  const displayTabRadius = Math.min(displayBaseWidth, displayBaseHeight) * tabRatio;
  const sourceTabMargin = Math.min(sourcePieceWidth, sourcePieceHeight) * tabRatio;

  // Full piece size including tab areas - use ADDITIVE margins
  // This ensures inner piece area equals base dimensions exactly
  const displayPieceWidth = displayBaseWidth + displayTabRadius * 2;
  const displayPieceHeight = displayBaseHeight + displayTabRadius * 2;

  // Canvas size for slicing (includes tab margin from source)
  const canvasWidth = sourcePieceWidth + sourceTabMargin * 2;
  const canvasHeight = sourcePieceHeight + sourceTabMargin * 2;

  // Generate random edge patterns for variety
  // verticalEdges[row][col] = true means piece(row,col) has TAB on right edge
  const verticalEdges = [];
  for (let r = 0; r < rows; r += 1) {
    verticalEdges[r] = [];
    for (let c = 0; c < cols - 1; c += 1) {
      verticalEdges[r][c] = Math.random() > 0.5;
    }
  }

  // horizontalEdges[row][col] = true means piece(row,col) has TAB on bottom edge
  const horizontalEdges = [];
  for (let r = 0; r < rows - 1; r += 1) {
    horizontalEdges[r] = [];
    for (let c = 0; c < cols; c += 1) {
      horizontalEdges[r][c] = Math.random() > 0.5;
    }
  }

  const pieces = [];

  for (let row = 0; row < rows; row += 1) {
    for (let col = 0; col < cols; col += 1) {
      const canvas = document.createElement('canvas');
      canvas.width = canvasWidth;
      canvas.height = canvasHeight;
      const ctx = canvas.getContext('2d');

      // Slice with margin for tab areas (using additive margin)
      const sourceX = col * sourcePieceWidth - sourceTabMargin;
      const sourceY = row * sourcePieceHeight - sourceTabMargin;

      ctx.drawImage(
        img,
        sourceX,
        sourceY,
        canvasWidth,
        canvasHeight,
        0,
        0,
        canvasWidth,
        canvasHeight,
      );

      // Correct position accounts for tab offset
      // Use min to match generateSvgPath's tabRadius calculation (keeps tabs square)
      const tabOffset = Math.min(displayBaseWidth, displayBaseHeight) * tabRatio;
      const correctX = col * displayBaseWidth - tabOffset;
      const correctY = row * displayBaseHeight - tabOffset;

      // Determine edge types based on random grids (complementary edges for neighbors)
      let topEdge = 'flat';
      if (row > 0) {
        topEdge = horizontalEdges[row - 1][col] ? 'slot' : 'tab';
      }

      let rightEdge = 'flat';
      if (col < cols - 1) {
        rightEdge = verticalEdges[row][col] ? 'tab' : 'slot';
      }

      let bottomEdge = 'flat';
      if (row < rows - 1) {
        bottomEdge = horizontalEdges[row][col] ? 'tab' : 'slot';
      }

      let leftEdge = 'flat';
      if (col > 0) {
        leftEdge = verticalEdges[row][col - 1] ? 'slot' : 'tab';
      }

      const edges = {
        top: topEdge,
        right: rightEdge,
        bottom: bottomEdge,
        left: leftEdge,
      };

      // Generate SVG path for this piece's jigsaw shape
      const svgPath = generateSvgPath(edges, displayPieceWidth, displayPieceHeight, pieceStyle);

      pieces.push({
        id: `${row}-${col}`,
        row,
        col,
        correctX,
        correctY,
        imageData: canvas.toDataURL('image/jpeg', 0.9),
        width: displayPieceWidth,
        height: displayPieceHeight,
        baseWidth: displayBaseWidth,
        baseHeight: displayBaseHeight,
        svgPath,
        // Connection tracking
        connections: {
          right: null,
          down: null,
          left: null,
          up: null,
        },
      });
    }
  }

  return pieces;
};

// Shuffle array using Fisher-Yates
const shuffleArray = (array) => {
  const shuffled = [...array];
  for (let i = shuffled.length - 1; i > 0; i -= 1) {
    const j = Math.floor(Math.random() * (i + 1));
    [shuffled[i], shuffled[j]] = [shuffled[j], shuffled[i]];
  }
  return shuffled;
};

const PuzzleEngineV2 = forwardRef(({
  imageData,
  rows,
  columns,
  containerWidth,
  soundEnabled,
  pieceStyle,
  isAlreadyComplete,
  savedPiecePositions,
  savedDeviceTier: _savedDeviceTier, // eslint-disable-line no-unused-vars
  savedCanvasWidth,
  onComplete,
  onProgress,
  onMove,
  onReady,
}, ref) => {
  // Map legacy styles to new styles
  const effectivePieceStyle = mapLegacyStyle(pieceStyle);
  const [pieces, setPieces] = useState([]);
  const [positions, setPositions] = useState({});
  const [isReady, setIsReady] = useState(false);
  const [draggedPiece, setDraggedPiece] = useState(null);
  const [puzzleDimensions, setPuzzleDimensions] = useState({ width: 0, height: 0 });
  const [zIndexCounter, setZIndexCounter] = useState(100);
  const [animatingPieces, setAnimatingPieces] = useState({});
  const [zoomLevel, setZoomLevel] = useState(1);
  const [panOffset, setPanOffset] = useState({ x: 0, y: 0 });

  const containerRef = useRef(null);
  const dragOffsetRef = useRef({ x: 0, y: 0 });
  const dragStartPosRef = useRef({}); // Store initial positions at drag start
  const piecesRef = useRef([]);
  const positionsRef = useRef({});
  const lastPinchDistanceRef = useRef(null);
  const pinchCenterRef = useRef(null);
  const isPanningRef = useRef(false);
  const lastPanPointRef = useRef(null);

  // Keep refs in sync with state
  useEffect(() => {
    piecesRef.current = pieces;
    positionsRef.current = positions;
  }, [pieces, positions]);

  // Get all pieces connected to a piece (recursive)
  const getConnectedGroup = useCallback((pieceId, visited = new Set()) => {
    if (visited.has(pieceId)) return [];
    visited.add(pieceId);

    const piece = piecesRef.current.find((p) => p.id === pieceId);
    if (!piece) return [pieceId];

    const group = [pieceId];
    const { connections } = piece;

    Object.values(connections).forEach((connectedId) => {
      if (connectedId) {
        group.push(...getConnectedGroup(connectedId, visited));
      }
    });

    return group;
  }, []);

  // Apply zoom with optional pinch center point
  const applyZoom = useCallback((newZoom, pinchCenter = null) => {
    const clampedZoom = Math.max(MIN_ZOOM, Math.min(MAX_ZOOM, newZoom));

    if (clampedZoom === 1) {
      setZoomLevel(1);
      setPanOffset({ x: 0, y: 0 });
      return;
    }

    if (pinchCenter) {
      // pinchCenter is already in container-relative coordinates
      const centerX = pinchCenter.x;
      const centerY = pinchCenter.y;

      const prevZoom = zoomLevel;
      const zoomDelta = clampedZoom / prevZoom;

      const newPanX = centerX - (centerX - panOffset.x) * zoomDelta;
      const newPanY = centerY - (centerY - panOffset.y) * zoomDelta;

      const maxPanX = puzzleDimensions.width * (clampedZoom - 1);
      const maxPanY = puzzleDimensions.height * (clampedZoom - 1);

      setPanOffset({
        x: Math.max(-maxPanX, Math.min(0, newPanX)),
        y: Math.max(-maxPanY, Math.min(0, newPanY)),
      });
    }

    setZoomLevel(clampedZoom);
  }, [zoomLevel, panOffset, puzzleDimensions]);

  // Animated zoom reset
  const handleZoomReset = useCallback(() => {
    const startZoom = zoomLevel;
    const startPan = { ...panOffset };
    const startTime = performance.now();
    const duration = 300;

    const animate = (currentTime) => {
      const elapsed = currentTime - startTime;
      const progress = Math.min(elapsed / duration, 1);
      const eased = 1 - ((1 - progress) ** 3); // Ease-out cubic

      const newZoom = startZoom + (1 - startZoom) * eased;
      const newPanX = startPan.x * (1 - eased);
      const newPanY = startPan.y * (1 - eased);

      setZoomLevel(newZoom);
      setPanOffset({ x: newPanX, y: newPanY });

      if (progress < 1) {
        requestAnimationFrame(animate);
      }
    };

    requestAnimationFrame(animate);
  }, [zoomLevel, panOffset]);

  // Check if two pieces should connect
  const shouldConnect = useCallback((piece1, piece2, pos1, pos2) => {
    const rowDiff = piece1.row - piece2.row;
    const colDiff = piece1.col - piece2.col;

    // Must be adjacent (not diagonal)
    if (Math.abs(rowDiff) + Math.abs(colDiff) !== 1) return null;

    // Check if positions are close enough
    // Positions are spaced by baseWidth (grid uses col * baseWidth - tabOffset)
    const expectedDeltaX = (piece2.col - piece1.col) * piece1.baseWidth;
    const expectedDeltaY = (piece2.row - piece1.row) * piece1.baseHeight;

    const actualDeltaX = pos2.x - pos1.x;
    const actualDeltaY = pos2.y - pos1.y;

    const distanceX = Math.abs(actualDeltaX - expectedDeltaX);
    const distanceY = Math.abs(actualDeltaY - expectedDeltaY);

    // Match PuzzleCanvas proximity: 20% of full piece size, minimum 10px
    const threshold = Math.max(10, piece1.width * 0.2);

    if (distanceX < threshold && distanceY < threshold) {
      // Return which direction piece2 is relative to piece1
      if (colDiff === -1) return 'right';
      if (colDiff === 1) return 'left';
      if (rowDiff === -1) return 'down';
      if (rowDiff === 1) return 'up';
    }

    return null;
  }, []);

  // Animate piece connection (shake + glow)
  const animateConnection = useCallback((pieceIds) => {
    const glowColor = BRAND_COLORS[Math.floor(Math.random() * BRAND_COLORS.length)];

    // Set animation state
    const animState = {};
    pieceIds.forEach((id) => {
      animState[id] = { glowColor, shake: true };
    });
    setAnimatingPieces((prev) => ({ ...prev, ...animState }));

    // Clear animation after duration
    setTimeout(() => {
      setAnimatingPieces((prev) => {
        const next = { ...prev };
        pieceIds.forEach((id) => {
          delete next[id];
        });
        return next;
      });
    }, 400);
  }, []);

  // Normalize all piece positions in a group to ensure perfect alignment
  // Called after connections to "heal" any accumulated positional drift
  const normalizeGroupPositions = useCallback((groupIds) => {
    if (groupIds.length <= 1) return;

    // Find anchor piece (top-left in grid)
    const groupPieces = groupIds
      .map((id) => piecesRef.current.find((p) => p.id === id))
      .filter(Boolean);

    if (groupPieces.length === 0) return;

    const anchor = groupPieces.reduce((topLeft, piece) => {
      if (piece.row < topLeft.row) return piece;
      if (piece.row === topLeft.row && piece.col < topLeft.col) return piece;
      return topLeft;
    });

    const anchorPos = positionsRef.current[anchor.id];
    if (!anchorPos) return;

    // Recalculate all positions based on anchor
    setPositions((prev) => {
      const next = { ...prev };
      groupPieces.forEach((piece) => {
        const colOffset = piece.col - anchor.col;
        const rowOffset = piece.row - anchor.row;
        next[piece.id] = {
          ...next[piece.id],
          x: Math.round(anchorPos.x + colOffset * piece.baseWidth),
          y: Math.round(anchorPos.y + rowOffset * piece.baseHeight),
        };
      });
      return next;
    });
  }, []);

  // Connect pieces and snap them together
  const connectPieces = useCallback((pieceId, targetId, direction) => {
    const piece = piecesRef.current.find((p) => p.id === pieceId);
    const target = piecesRef.current.find((p) => p.id === targetId);

    if (!piece || !target) return;

    // Already connected?
    if (piece.connections[direction] === targetId) return;

    // Get target group BEFORE updating connections (so it doesn't include the dragged piece)
    const targetGroup = getConnectedGroup(targetId);

    // Update connection references
    const opposites = {
      right: 'left', left: 'right', up: 'down', down: 'up',
    };
    piece.connections[direction] = targetId;
    target.connections[opposites[direction]] = pieceId;

    // Snap target group to align with piece
    const currentPos = positionsRef.current[pieceId];
    const targetPos = positionsRef.current[targetId];

    // Calculate where target should be relative to piece
    // Positions are spaced by baseWidth (grid uses col * baseWidth - tabOffset)
    let expectedX = currentPos.x;
    let expectedY = currentPos.y;

    if (direction === 'right') expectedX += piece.baseWidth;
    if (direction === 'left') expectedX -= piece.baseWidth;
    if (direction === 'down') expectedY += piece.baseHeight;
    if (direction === 'up') expectedY -= piece.baseHeight;

    // Round to prevent floating-point drift accumulation
    expectedX = Math.round(expectedX);
    expectedY = Math.round(expectedY);

    // Move entire connected group (round delta to prevent fractional drift)
    const deltaX = Math.round(expectedX - targetPos.x);
    const deltaY = Math.round(expectedY - targetPos.y);

    setPositions((prev) => {
      const next = { ...prev };
      targetGroup.forEach((id) => {
        if (next[id]) {
          next[id] = {
            ...next[id],
            x: Math.round(next[id].x + deltaX),
            y: Math.round(next[id].y + deltaY),
          };
        }
      });
      return next;
    });

    // Play sound and animate
    if (soundEnabled) {
      playConnectSound();
    }
    animateConnection([pieceId, targetId]);

    // Normalize entire merged group to ensure perfect alignment
    // This "heals" any accumulated positional drift
    const mergedGroup = getConnectedGroup(pieceId);
    normalizeGroupPositions(mergedGroup);
  }, [getConnectedGroup, soundEnabled, animateConnection, normalizeGroupPositions]);

  // Check for possible connections after drag
  const checkConnections = useCallback((draggedId) => {
    const draggedGroup = getConnectedGroup(draggedId);
    const draggedPieces = draggedGroup.map((id) => piecesRef.current.find((p) => p.id === id));

    // Check each piece in dragged group against all other pieces
    draggedPieces.forEach((piece) => {
      if (!piece) return;
      const pos = positionsRef.current[piece.id];

      piecesRef.current.forEach((other) => {
        if (draggedGroup.includes(other.id)) return; // Skip pieces in same group
        const otherPos = positionsRef.current[other.id];

        const direction = shouldConnect(piece, other, pos, otherPos);
        if (direction && !piece.connections[direction]) {
          connectPieces(piece.id, other.id, direction);
        }
      });
    });
  }, [getConnectedGroup, shouldConnect, connectPieces]);

  // Format piece positions for save state (matches PuzzleCanvas format)
  // Defined before checkCompletion so it can be passed with onComplete callback
  const formatPiecePositions = useCallback(() => {
    const formattedPieces = piecesRef.current.map((piece) => {
      const pos = positionsRef.current[piece.id];
      return {
        id: piece.id,
        x: pos?.x || 0,
        y: pos?.y || 0,
        zIndex: pos?.zIndex || 1,
        rightConnectionId: piece.connections.right || null,
        downConnectionId: piece.connections.down || null,
        leftConnectionId: piece.connections.left || null,
        upConnectionId: piece.connections.up || null,
      };
    });
    return {
      deviceTier: getDeviceTier(),
      pieces: formattedPieces,
      canvasWidth: puzzleDimensions.width,
    };
  }, [puzzleDimensions.width]);

  // Check if puzzle is complete (returns true if completed)
  const checkCompletion = useCallback(() => {
    // All pieces should be in one connected group
    if (piecesRef.current.length === 0) return false;

    const firstPieceId = piecesRef.current[0].id;
    const connectedGroup = getConnectedGroup(firstPieceId);

    // Puzzle is complete when ALL pieces are connected (one group)
    if (connectedGroup.length === piecesRef.current.length && onComplete) {
      // Get preview image (null is handled by calling code)
      const previewImage = null;
      // Pass final piece positions so they can be saved accurately at completion
      onComplete(previewImage, formatPiecePositions());
      return true;
    }
    return false;
  }, [getConnectedGroup, onComplete, formatPiecePositions]);

  // Initialize puzzle
  useEffect(() => {
    if (!imageData) return;

    const img = new Image();
    img.onload = () => {
      // Calculate canvas dimensions like PuzzleCanvas
      const canvasWidth = Math.min(containerWidth * 0.98, 1000);
      const aspectRatio = img.naturalWidth / img.naturalHeight;
      const canvasHeight = canvasWidth / aspectRatio;

      // Calculate piece size so all pieces fit when shuffled (like PuzzleCanvas)
      const basePieceSize = calculatePieceSize(canvasWidth, canvasHeight, columns, rows);

      // Slice image with calculated piece size
      const slicedPieces = sliceImage(img, rows, columns, basePieceSize, effectivePieceStyle);

      const pieceWidth = slicedPieces[0].width;
      const pieceHeight = slicedPieces[0].height;

      // Use full canvas dimensions for puzzle area (not tiny baseWidth * columns)
      setPuzzleDimensions({ width: canvasWidth, height: canvasHeight });

      // Completed puzzles always use perfect grid positions (correctX/correctY)
      // to avoid stale saved positions from the race between final drag and completion
      const shouldShowComplete = isAlreadyComplete;
      const shouldRestore = !isAlreadyComplete
        && savedPiecePositions && savedPiecePositions.length > 0;

      const initialPositions = {};

      if (shouldRestore) {
        // Restore saved positions with scaling if needed
        // This works for BOTH in-progress AND completed puzzles with saved state
        const scale = savedCanvasWidth ? canvasWidth / savedCanvasWidth : 1;

        savedPiecePositions.forEach((savedPiece) => {
          const piece = slicedPieces.find((p) => p.id === savedPiece.id);
          if (piece) {
            initialPositions[savedPiece.id] = {
              x: Math.round((savedPiece.x || 0) * scale),
              y: Math.round((savedPiece.y || 0) * scale),
              zIndex: savedPiece.zIndex || 1,
            };
            // Restore all 4 connections
            if (savedPiece.rightConnectionId) {
              piece.connections.right = savedPiece.rightConnectionId;
            }
            if (savedPiece.downConnectionId) {
              piece.connections.down = savedPiece.downConnectionId;
            }
            if (savedPiece.leftConnectionId) {
              piece.connections.left = savedPiece.leftConnectionId;
            }
            if (savedPiece.upConnectionId) {
              piece.connections.up = savedPiece.upConnectionId;
            }
          }
        });
      } else if (shouldShowComplete) {
        // Calculate bounding box of assembled puzzle for centering
        const minX = Math.min(...slicedPieces.map((p) => p.correctX));
        const maxX = Math.max(...slicedPieces.map((p) => p.correctX + p.width));
        const minY = Math.min(...slicedPieces.map((p) => p.correctY));
        const assembledWidth = maxX - minX;

        // Center horizontally, ~20px from top
        const offsetX = (canvasWidth - assembledWidth) / 2 - minX;
        const offsetY = 20 - minY;

        slicedPieces.forEach((piece, index) => {
          initialPositions[piece.id] = {
            x: piece.correctX + offsetX,
            y: piece.correctY + offsetY,
            zIndex: index + 1,
          };
          // Mark all connections for completed puzzle
          const pieceRow = piece.row;
          const pieceCol = piece.col;
          const { connections } = piece;
          if (pieceCol < columns - 1) {
            const rightNeighbor = slicedPieces.find(
              (p) => p.row === pieceRow && p.col === pieceCol + 1,
            );
            if (rightNeighbor) connections.right = rightNeighbor.id;
          }
          if (pieceRow < rows - 1) {
            const downNeighbor = slicedPieces.find(
              (p) => p.row === pieceRow + 1 && p.col === pieceCol,
            );
            if (downNeighbor) connections.down = downNeighbor.id;
          }
          if (pieceCol > 0) {
            const leftNeighbor = slicedPieces.find(
              (p) => p.row === pieceRow && p.col === pieceCol - 1,
            );
            if (leftNeighbor) connections.left = leftNeighbor.id;
          }
          if (pieceRow > 0) {
            const upNeighbor = slicedPieces.find(
              (p) => p.row === pieceRow - 1 && p.col === pieceCol,
            );
            if (upNeighbor) connections.up = upNeighbor.id;
          }
        });
      } else {
        // Shuffle and position pieces in a grid
        const shuffled = shuffleArray(slicedPieces);
        const gridCols = Math.ceil(Math.sqrt(shuffled.length * (canvasWidth / canvasHeight)));
        const cellWidth = canvasWidth / gridCols;
        const cellHeight = canvasHeight / Math.ceil(shuffled.length / gridCols);

        shuffled.forEach((piece, index) => {
          const gridRow = Math.floor(index / gridCols);
          const gridCol = index % gridCols;

          // Center piece in cell with small random offset
          const offsetX = (Math.random() - 0.5) * 10;
          const offsetY = (Math.random() - 0.5) * 10;

          initialPositions[piece.id] = {
            x: Math.round(gridCol * cellWidth + (cellWidth - pieceWidth) / 2 + offsetX),
            y: Math.round(gridRow * cellHeight + (cellHeight - pieceHeight) / 2 + offsetY),
            zIndex: index + 1,
          };
        });
      }

      setPieces(slicedPieces);
      setPositions(initialPositions);
      setIsReady(true);

      if (onReady) {
        onReady();
      }
    };

    img.src = imageData;
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [imageData, rows, columns, containerWidth, effectivePieceStyle]);

  // Normalize all connected groups after restoring saved positions
  // This "heals" any alignment gaps from scaling or floating-point drift
  useEffect(() => {
    if (!isReady || !pieces.length) return;

    // Only normalize if we restored from saved state (has connections)
    const hasConnections = pieces.some((p) => (
      p.connections.right || p.connections.down || p.connections.left || p.connections.up
    ));
    if (!hasConnections) return;

    // Find all unique connected groups and normalize each
    const visited = new Set();
    pieces.forEach((piece) => {
      if (visited.has(piece.id)) return;
      const group = getConnectedGroup(piece.id);
      group.forEach((id) => visited.add(id));
      if (group.length > 1) {
        normalizeGroupPositions(group);
      }
    });
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isReady]);

  // Handle drag start
  const handleDragStart = useCallback((e, pieceId) => {
    // Don't allow dragging on completed puzzles
    if (isAlreadyComplete) return;

    e.preventDefault();

    const newZIndex = zIndexCounter + 1;
    setZIndexCounter(newZIndex);

    // Bring entire connected group to front
    const group = getConnectedGroup(pieceId);
    setPositions((prev) => {
      const next = { ...prev };
      group.forEach((id, i) => {
        if (next[id]) {
          next[id] = { ...next[id], zIndex: newZIndex + i };
        }
      });
      return next;
    });

    // Store initial positions for entire group (fixes race condition during drag)
    const startPositions = {};
    group.forEach((id) => {
      startPositions[id] = { ...positionsRef.current[id] };
    });
    dragStartPosRef.current = {
      positions: startPositions,
      mouseX: e.clientX,
      mouseY: e.clientY,
    };

    setDraggedPiece(pieceId);

    const rect = e.currentTarget.getBoundingClientRect();
    dragOffsetRef.current = {
      x: e.clientX - rect.left,
      y: e.clientY - rect.top,
    };

    if (e.dataTransfer) {
      e.dataTransfer.setDragImage(new Image(), 0, 0);
    }
  }, [zIndexCounter, getConnectedGroup, isAlreadyComplete]);

  // Handle drag - uses initial position + mouse delta to avoid race conditions
  const handleDrag = useCallback((e) => {
    if (!draggedPiece || !containerRef.current) return;
    if (e.clientX === 0 && e.clientY === 0) return;

    const {
      positions: startPositions, mouseX: startMouseX, mouseY: startMouseY,
    } = dragStartPosRef.current;
    if (!startPositions) return;

    // Calculate mouse delta from drag start (not from last frame)
    const mouseDeltaX = e.clientX - startMouseX;
    const mouseDeltaY = e.clientY - startMouseY;

    // Move entire connected group based on initial positions + mouse delta
    const group = getConnectedGroup(draggedPiece);

    setPositions((prev) => {
      const next = { ...prev };

      // Step 1: Calculate raw positions for all pieces in group
      const rawPositions = {};
      group.forEach((id) => {
        const startPos = startPositions[id];
        if (startPos) {
          rawPositions[id] = {
            x: startPos.x + mouseDeltaX,
            y: startPos.y + mouseDeltaY,
          };
        }
      });

      // Step 2: Find group bounding box
      let minX = Infinity;
      let minY = Infinity;
      let maxX = -Infinity;
      let maxY = -Infinity;
      group.forEach((id) => {
        const pos = rawPositions[id];
        const piece = piecesRef.current.find((p) => p.id === id);
        if (pos && piece) {
          minX = Math.min(minX, pos.x);
          minY = Math.min(minY, pos.y);
          maxX = Math.max(maxX, pos.x + piece.width);
          maxY = Math.max(maxY, pos.y + piece.height);
        }
      });

      // Step 3: Calculate adjustment to keep entire group in bounds
      let adjustX = 0;
      let adjustY = 0;
      if (minX < 0) adjustX = -minX;
      else if (maxX > puzzleDimensions.width) adjustX = puzzleDimensions.width - maxX;
      if (minY < 0) adjustY = -minY;
      else if (maxY > puzzleDimensions.height) adjustY = puzzleDimensions.height - maxY;

      // Step 4: Apply same adjustment to all pieces (keeps group together)
      group.forEach((id) => {
        const pos = rawPositions[id];
        if (pos && next[id]) {
          next[id] = {
            ...next[id],
            x: Math.round(pos.x + adjustX),
            y: Math.round(pos.y + adjustY),
          };
        }
      });

      return next;
    });
  }, [draggedPiece, getConnectedGroup, puzzleDimensions]);

  // Handle drag end
  const handleDragEnd = useCallback(() => {
    if (!draggedPiece) return;

    checkConnections(draggedPiece);
    const justCompleted = checkCompletion();

    if (onMove) {
      onMove();
    }

    // Don't fire progress update if puzzle just completed
    // (handleComplete saves the correct isComplete: true to localStorage;
    // firing onProgress here would overwrite it with isComplete: false)
    if (onProgress && !justCompleted) {
      onProgress({
        isComplete: false,
        piecePositions: formatPiecePositions(),
      });
    }

    setDraggedPiece(null);
  }, [draggedPiece, checkConnections, checkCompletion, onMove, onProgress, formatPiecePositions]);

  // Touch handlers with pinch-zoom and pan support
  const handleContainerTouchStart = useCallback((e) => {
    if (e.touches.length === 2) {
      // Prevent default to stop Safari's built-in gesture handling
      e.preventDefault();

      // Two-finger pinch start
      const touch1 = e.touches[0];
      const touch2 = e.touches[1];
      const distance = Math.hypot(
        touch2.clientX - touch1.clientX,
        touch2.clientY - touch1.clientY,
      );
      lastPinchDistanceRef.current = distance;

      // Store container-relative coordinates for pinch center
      const rect = containerRef.current?.getBoundingClientRect();
      pinchCenterRef.current = {
        x: ((touch1.clientX + touch2.clientX) / 2) - (rect?.left || 0),
        y: ((touch1.clientY + touch2.clientY) / 2) - (rect?.top || 0),
      };
      isPanningRef.current = false;
    }
  }, []);

  const handleTouchStart = useCallback((e, pieceId) => {
    // Check for pinch gesture first
    if (e.touches.length === 2) {
      handleContainerTouchStart(e);
      return;
    }

    // Single-finger: if zoomed and not on a piece, start panning
    if (zoomLevel > 1 && !pieceId) {
      isPanningRef.current = true;
      lastPanPointRef.current = {
        x: e.touches[0].clientX,
        y: e.touches[0].clientY,
      };
      return;
    }

    // Otherwise, handle piece drag
    if (pieceId) {
      const touch = e.touches[0];
      handleDragStart({
        preventDefault: () => {},
        clientX: touch.clientX,
        clientY: touch.clientY,
        currentTarget: e.currentTarget,
      }, pieceId);
    }
  }, [zoomLevel, handleDragStart, handleContainerTouchStart]);

  const handleTouchMove = useCallback((e) => {
    // Handle pinch zoom
    if (e.touches.length === 2 && lastPinchDistanceRef.current !== null) {
      e.preventDefault();
      const touch1 = e.touches[0];
      const touch2 = e.touches[1];
      const distance = Math.hypot(
        touch2.clientX - touch1.clientX,
        touch2.clientY - touch1.clientY,
      );

      const scale = distance / lastPinchDistanceRef.current;
      const newZoom = zoomLevel * scale;

      applyZoom(newZoom, pinchCenterRef.current);
      lastPinchDistanceRef.current = distance;
      return;
    }

    // Handle pan when zoomed
    if (isPanningRef.current && lastPanPointRef.current && zoomLevel > 1) {
      e.preventDefault();
      const dx = e.touches[0].clientX - lastPanPointRef.current.x;
      const dy = e.touches[0].clientY - lastPanPointRef.current.y;

      const maxPanX = puzzleDimensions.width * (zoomLevel - 1);
      const maxPanY = puzzleDimensions.height * (zoomLevel - 1);

      setPanOffset((prev) => ({
        x: Math.max(-maxPanX, Math.min(0, prev.x + dx)),
        y: Math.max(-maxPanY, Math.min(0, prev.y + dy)),
      }));

      lastPanPointRef.current = {
        x: e.touches[0].clientX,
        y: e.touches[0].clientY,
      };
      return;
    }

    // Handle piece drag
    if (!draggedPiece || !containerRef.current) return;
    e.preventDefault();

    const touch = e.touches[0];
    handleDrag({ clientX: touch.clientX, clientY: touch.clientY });
  }, [draggedPiece, handleDrag, zoomLevel, applyZoom, puzzleDimensions]);

  const handleTouchEnd = useCallback(() => {
    lastPinchDistanceRef.current = null;
    pinchCenterRef.current = null;

    if (isPanningRef.current) {
      isPanningRef.current = false;
      lastPanPointRef.current = null;
      return;
    }

    handleDragEnd();
  }, [handleDragEnd]);

  // Expose methods via ref
  useImperativeHandle(ref, () => ({
    shuffle: () => {
      // Shuffle preserving connected groups - largest group centered, others scattered
      // Uses grid-based placement (like PuzzleCanvas) - no collision detection needed
      if (piecesRef.current.length === 0) return;

      // Step 1: Find all connected groups
      const visited = new Set();
      const groups = [];

      piecesRef.current.forEach((piece) => {
        if (!visited.has(piece.id)) {
          const group = getConnectedGroup(piece.id);
          group.forEach((id) => visited.add(id));
          groups.push(group);
        }
      });

      // Step 2: Sort by size (largest first)
      groups.sort((a, b) => b.length - a.length);

      // Step 3: Calculate visual piece size (piece.width/height already include tabs)
      const firstPiece = piecesRef.current[0];
      const visualPieceSize = firstPiece
        ? Math.max(firstPiece.width, firstPiece.height)
        : 50;
      const halfVisual = visualPieceSize / 2;

      // Step 4: Helper - get group bounding box
      const getGroupBounds = (groupIds) => {
        let minX = Infinity;
        let maxX = -Infinity;
        let minY = Infinity;
        let maxY = -Infinity;
        groupIds.forEach((id) => {
          const pos = positionsRef.current[id];
          const piece = piecesRef.current.find((p) => p.id === id);
          if (pos && piece) {
            minX = Math.min(minX, pos.x);
            maxX = Math.max(maxX, pos.x + piece.width);
            minY = Math.min(minY, pos.y);
            maxY = Math.max(maxY, pos.y + piece.height);
          }
        });
        return {
          minX, maxX, minY, maxY,
        };
      };

      // Step 5: Helper - move group centroid to target position (clamped to canvas)
      const moveGroupTo = (groupIds, targetX, targetY) => {
        const bounds = getGroupBounds(groupIds);
        const halfWidth = (bounds.maxX - bounds.minX) / 2 + halfVisual;
        const halfHeight = (bounds.maxY - bounds.minY) / 2 + halfVisual;

        // Clamp to keep group within canvas
        const clampedX = Math.max(
          halfWidth,
          Math.min(puzzleDimensions.width - halfWidth, targetX),
        );
        const clampedY = Math.max(
          halfHeight,
          Math.min(puzzleDimensions.height - halfHeight, targetY),
        );

        const centroidX = (bounds.minX + bounds.maxX) / 2;
        const centroidY = (bounds.minY + bounds.maxY) / 2;
        const dx = clampedX - centroidX;
        const dy = clampedY - centroidY;

        const moved = {};
        groupIds.forEach((id) => {
          const pos = positionsRef.current[id];
          if (pos) {
            moved[id] = { ...pos, x: pos.x + dx, y: pos.y + dy };
          }
        });
        return moved;
      };

      // Step 6: Center the largest group
      const centerX = puzzleDimensions.width / 2;
      const centerY = puzzleDimensions.height / 2;
      const largestGroup = groups[0];

      const newPositions = { ...positionsRef.current };
      const largestMoved = moveGroupTo(largestGroup, centerX, centerY);
      Object.assign(newPositions, largestMoved);
      positionsRef.current = newPositions;

      // Step 7: Define reserved zone around centered group
      const bounds = getGroupBounds(largestGroup);
      const groupWidth = bounds.maxX - bounds.minX + visualPieceSize;
      const groupHeight = bounds.maxY - bounds.minY + visualPieceSize;
      const reservedPadding = visualPieceSize * 0.3;

      const reservedLeft = centerX - groupWidth / 2 - reservedPadding;
      const reservedRight = centerX + groupWidth / 2 + reservedPadding;
      const reservedTop = centerY - groupHeight / 2 - reservedPadding;
      const reservedBottom = centerY + groupHeight / 2 + reservedPadding;

      // Step 8: Place remaining groups using grid-based approach
      if (groups.length > 1) {
        const remainingGroups = groups.slice(1);
        const numRemaining = remainingGroups.length;

        // Minimum cell size = piece size * spacing factor (20% extra space)
        const SPACING_FACTOR = 1.2;
        const minCellSize = visualPieceSize * SPACING_FACTOR;

        // Calculate max grid density that still provides adequate spacing
        const maxGridCols = Math.max(2, Math.floor(puzzleDimensions.width / minCellSize));
        const maxGridRows = Math.max(2, Math.floor(puzzleDimensions.height / minCellSize));

        // Calculate initial grid size based on canvas aspect ratio
        const canvasAspect = puzzleDimensions.width / puzzleDimensions.height;
        let gridCols = Math.min(
          maxGridCols,
          Math.ceil(Math.sqrt((numRemaining + 4) * canvasAspect)),
        );
        let gridRows = Math.min(
          maxGridRows,
          Math.ceil((numRemaining + 4) / gridCols),
        );
        let cellWidth = puzzleDimensions.width / gridCols;
        let cellHeight = puzzleDimensions.height / gridRows;

        // Generate grid positions, excluding reserved zone
        const generatePositions = () => {
          const gridPositions = [];
          for (let r = 0; r < gridRows; r += 1) {
            for (let c = 0; c < gridCols; c += 1) {
              const x = cellWidth * (c + 0.5);
              const y = cellHeight * (r + 0.5);
              // Skip if inside reserved zone
              if (x >= reservedLeft && x <= reservedRight
                  && y >= reservedTop && y <= reservedBottom) {
                // eslint-disable-next-line no-continue
                continue;
              }
              gridPositions.push({ x, y });
            }
          }
          return gridPositions;
        };

        let availablePositions = generatePositions();

        // Increase grid density until we have enough positions
        // But don't exceed max that would make cells too small
        while (availablePositions.length < numRemaining
            && gridCols < maxGridCols && gridRows < maxGridRows) {
          gridCols = Math.min(maxGridCols, gridCols + 1);
          gridRows = Math.min(maxGridRows, gridRows + 1);
          cellWidth = puzzleDimensions.width / gridCols;
          cellHeight = puzzleDimensions.height / gridRows;
          availablePositions = generatePositions();
        }

        // Fisher-Yates shuffle positions for variety
        for (let i = availablePositions.length - 1; i > 0; i -= 1) {
          const j = Math.floor(Math.random() * (i + 1));
          [availablePositions[i], availablePositions[j]] = [
            availablePositions[j], availablePositions[i],
          ];
        }

        // Place each group at its assigned grid position
        remainingGroups.forEach((groupIds, index) => {
          if (availablePositions[index]) {
            const moved = moveGroupTo(
              groupIds,
              availablePositions[index].x,
              availablePositions[index].y,
            );
            Object.assign(newPositions, moved);
            positionsRef.current = newPositions;
          }
        });
      }

      setPositions(newPositions);
    },
    getSnapshot: () => {
      // Create a snapshot of the current puzzle state as a data URL
      // This is used for preview images in save state
      // For now, return null - the calling code handles missing snapshots
      // A full implementation would use html2canvas or similar
      if (!containerRef.current) return null;
      return null;
    },
    zoomReset: handleZoomReset,
  }), [puzzleDimensions, getConnectedGroup, handleZoomReset]);

  if (!isReady) {
    return (
      <div
        className="flex items-center justify-center bg-puzzle-muted rounded-lg"
        style={{ width: containerWidth * 0.98, height: containerWidth * 0.65 }}
      >
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600" />
      </div>
    );
  }

  return (
    <div className="flex flex-col items-center">
      {/* SVG definitions for clip paths */}
      <svg style={{ position: 'absolute', width: 0, height: 0 }}>
        <defs>
          {pieces.map((piece) => (
            <clipPath key={piece.id} id={`clip-${piece.id}`} clipPathUnits="objectBoundingBox">
              <path
                d={piece.svgPath}
                transform={`scale(${1 / piece.width}, ${1 / piece.height})`}
              />
            </clipPath>
          ))}
        </defs>
      </svg>

      {/* Zoom reset button - shown when zoomed */}
      {zoomLevel > 1 && (
        <button
          type="button"
          onClick={handleZoomReset}
          className="absolute top-2 right-2 bg-white/90 hover:bg-white rounded-full p-2 shadow-lg z-50 transition-colors"
          aria-label="Reset zoom"
        >
          <svg className="w-5 h-5 text-gray-700" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0zM13 10H7" />
          </svg>
        </button>
      )}

      {/* eslint-disable-next-line jsx-a11y/no-noninteractive-element-interactions */}
      <div
        ref={containerRef}
        role="region"
        aria-label="Puzzle board"
        className="relative bg-puzzle-muted rounded-lg overflow-visible"
        style={{
          width: puzzleDimensions.width,
          height: puzzleDimensions.height,
          touchAction: 'none',
          isolation: 'isolate',
          transform: `scale(${zoomLevel}) translate(${panOffset.x / zoomLevel}px, ${panOffset.y / zoomLevel}px)`,
          transformOrigin: '0 0',
        }}
        onMouseMove={draggedPiece ? handleDrag : undefined}
        onMouseUp={handleDragEnd}
        onMouseLeave={handleDragEnd}
        onTouchStart={handleContainerTouchStart}
        onTouchMove={handleTouchMove}
        onTouchEnd={handleTouchEnd}
      >
        {/* Puzzle pieces */}
        {pieces.map((piece) => {
          const pos = positions[piece.id];
          if (!pos) return null;

          const anim = animatingPieces[piece.id];
          const isAnimating = !!anim;

          return (
            <div
              key={piece.id}
              role="button"
              tabIndex={0}
              aria-label={`Puzzle piece ${piece.row + 1}, ${piece.col + 1}`}
              onMouseDown={(e) => handleDragStart(e, piece.id)}
              onTouchStart={(e) => handleTouchStart(e, piece.id)}
              onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                  e.preventDefault();
                }
              }}
              className={`absolute select-none cursor-grab active:cursor-grabbing ${
                isAnimating ? 'animate-shake' : ''
              }`}
              style={{
                left: pos.x,
                top: pos.y,
                width: piece.width,
                height: piece.height,
                zIndex: pos.zIndex || 1,
                filter: isAnimating
                  ? `drop-shadow(0 0 15px ${anim.glowColor})`
                  : 'none',
                transition: isAnimating ? 'filter 0.4s ease-out' : 'none',
              }}
            >
              {/* Clipped image */}
              <img
                src={piece.imageData}
                alt={`Piece ${piece.id}`}
                style={{
                  width: '100%',
                  height: '100%',
                  clipPath: `url(#clip-${piece.id})`,
                }}
                draggable={false}
              />
              {/* SVG stroke overlay for border */}
              <svg
                style={{
                  position: 'absolute',
                  top: 0,
                  left: 0,
                  width: '100%',
                  height: '100%',
                  pointerEvents: 'none',
                }}
                viewBox={`0 0 ${piece.width} ${piece.height}`}
                preserveAspectRatio="none"
              >
                <path
                  d={piece.svgPath}
                  fill="none"
                  stroke="rgba(0,0,0,0.15)"
                  strokeWidth="0.75"
                />
              </svg>
            </div>
          );
        })}
      </div>

      {/* Progress indicator */}
      <div className="mt-4 text-sm text-puzzle-text-secondary">
        {(() => {
          const groups = new Set();
          pieces.forEach((p) => {
            const group = getConnectedGroup(p.id);
            groups.add(group.sort().join(','));
          });
          return `${pieces.length - groups.size + 1} connections made`;
        })()}
      </div>

      {/* CSS for shake animation */}
      <style>
        {`
          @keyframes shake {
            0%, 100% { transform: translateX(0); }
            20% { transform: translateX(-3px); }
            40% { transform: translateX(3px); }
            60% { transform: translateX(-2px); }
            80% { transform: translateX(2px); }
          }
          .animate-shake {
            animation: shake 0.2s ease-in-out;
          }
        `}
      </style>
    </div>
  );
});

PuzzleEngineV2.propTypes = {
  imageData: PropTypes.string.isRequired,
  rows: PropTypes.number,
  columns: PropTypes.number,
  containerWidth: PropTypes.number.isRequired,
  soundEnabled: PropTypes.bool,
  pieceStyle: PropTypes.string, // Accepts legacy styles too, will be mapped
  isAlreadyComplete: PropTypes.bool,
  savedPiecePositions: PropTypes.arrayOf(PropTypes.shape({
    id: PropTypes.string,
    x: PropTypes.number,
    y: PropTypes.number,
    zIndex: PropTypes.number,
    rightConnectionId: PropTypes.string,
    downConnectionId: PropTypes.string,
    leftConnectionId: PropTypes.string,
    upConnectionId: PropTypes.string,
  })),
  savedDeviceTier: PropTypes.string,
  savedCanvasWidth: PropTypes.number,
  onComplete: PropTypes.func,
  onProgress: PropTypes.func,
  onMove: PropTypes.func,
  onReady: PropTypes.func,
};

PuzzleEngineV2.defaultProps = {
  rows: 3,
  columns: 3,
  soundEnabled: true,
  pieceStyle: 'traditional',
  isAlreadyComplete: false,
  savedPiecePositions: null,
  savedDeviceTier: null,
  savedCanvasWidth: null,
  onComplete: null,
  onProgress: null,
  onMove: null,
  onReady: null,
};

export default PuzzleEngineV2;
